// One row per active bill for the Bulk Bill Run board, and the source of every
// bill's status on the Bills list too: both read buildBoardRows, so the two
// pages cannot disagree about where a bill is.

import type { PipelineData } from '../services/bulkReportService';
import type { BvBill } from '../types/billTypes';
import type { ParseState } from '../utils/parseState';
import { parseSubjectsByBill, billParseStateOf } from '../utils/billParseStatus';
import { billPipeline, analysisFinder, twinCounter, type PipelineResult, type Twins } from './billPipeline';
import {
  reportBucket, reportFigures, reportSendStatus, NO_SENDS, type ReportBucket, type ReportSendStatus,
} from './reportStatus';
import { billWorkflowStatus, WORKFLOW_ORDER, type BillWorkflowStatus } from './billWorkflow';
import { billVerdict, type BillVerdict } from './billVerdict';
import { billStatus, type BillStatus } from '../utils/billStatus';

export type BoardRow = {
  bill: BvBill;
  pipeline: PipelineResult;
  /** The bill's status (lib/billWorkflow). */
  status: BillWorkflowStatus;
  sendStatus: ReportSendStatus;
  /** The report's verdict bucket; null before there is a report. */
  bucket: ReportBucket | null;
  /** The Bills list's verdict (utils/billStatus), for the shared Verdict filter. */
  verdict: BillStatus;
  parseState: ParseState;
  /** Inbox emails carrying this bill's invoice — what a re-parse runs on. */
  emailIds: string[];
  /** Other active bills for this exact site and period. */
  twins: Twins;
};

/** `verdictFor` is useBillVerdicts' lookup, with each retailer's charge map;
 *  omitted, verdicts are worked out without the map. */
export function buildBoardRows(
  data: PipelineData,
  verdictFor: (bill: BvBill) => BillVerdict | null = billVerdict,
): BoardRow[] {
  const subjects = parseSubjectsByBill(data.emails);
  const queueByBill = new Map(data.queue.map((q) => [q.bill_id, q]));
  const findAnalysis = analysisFinder(data.analyses);
  const twinsOf = twinCounter(data.bills);
  const emailIdsByBill = new Map<string, string[]>();
  for (const e of data.emails) {
    if (!e.bill_id || !e.id) continue;
    emailIdsByBill.set(e.bill_id, [...(emailIdsByBill.get(e.bill_id) ?? []), e.id]);
  }

  const rows = data.bills.map((bill): BoardRow => {
    const parseState = billParseStateOf(bill, subjects.get(bill.id));
    const twins = twinsOf(bill);
    const pipeline = billPipeline({
      bill,
      parseState,
      queue: queueByBill.get(bill.id) ?? null,
      analysis: findAnalysis(bill),
      twins,
    });
    const verdict = verdictFor(bill);
    const sendStatus = reportSendStatus(bill, data.sendHistory[bill.id] ?? NO_SENDS, verdict);
    const figures = pipeline.current === 'done' ? reportFigures(bill) : null;
    const bucket = figures ? reportBucket(figures, verdict) : null;
    const status = billWorkflowStatus({
      current: pipeline.current,
      bucket,
      sendStatus,
      step: bill.workflow_status ?? null,
    });
    return {
      bill, parseState, twins, emailIds: emailIdsByBill.get(bill.id) ?? [], pipeline, status, sendStatus,
      bucket, verdict: billStatus(bill, verdict),
    };
  });

  // Earliest status first; newest period first within a status.
  return rows.sort((a, b) =>
    WORKFLOW_ORDER.indexOf(a.status) - WORKFLOW_ORDER.indexOf(b.status)
    || String(b.bill.bill_from ?? '').localeCompare(String(a.bill.bill_from ?? '')));
}
